import {
  Button,
  Col,
  DatePicker,
  Form,
  Layout,
  message,
  Row,
  Tabs,
  Table,
  Dropdown,
  Menu
} from "antd";
import { ChevronDown, Eye, Pen } from "lucide-react";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatString,
  getDateRange,
  navigateTo,
  getUserRole
} from "../../Utils/Common";
import { hasRole, hasExactRole } from "../../Utils/AccessControl";
import { postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PurchaseOrder = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const dateRange = getDateRange("month");
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [activeTab, setActiveTab] = useState("pending");

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "purchase_orders/get",
    initialFilters: {
      date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
      date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
      status: "pending",
    },
    isFormData: true,
  });

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      status: activeKey,
      page: 1,
    }));
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2) {
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: "",
          date_to: "",
          page: 1,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0]?.startOf("month").format("YYYY-MM-DD"),
        date_to: value[1]?.endOf("month").format("YYYY-MM-DD"),
        page: 1,
      }));
      return;
    }

    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ?? "",
      page: 1,
    }));
  }

  function getTableColumns() {
    switch (activeTab) {
      case "pending":
        return [
          {
            title: "Purchase Order #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Purchase Request #",
            dataIndex: "purchase_request_id",
            key: "purchase_request_id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Created By",
            dataIndex: "added_by_name",
            key: "added_by_name",
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => {
              const menu = (
                <Menu>
                  {hasRole(getUserRole(), "admin") && (
                    <>
                      {/* <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() =>
                          navigateTo(`/purchase-order/view/${record.id}`)
                        }
                      >
                        View
                      </Menu.Item> */}
                      <Menu.Item
                        key="edit"
                        icon={<Pen strokeWidth={1.5} size={15} />}
                        onClick={() =>
                          navigateTo(`/purchase-order/edit/${record.id}`)
                        }
                      >
                        Edit
                      </Menu.Item>
                      
                      <Menu.Item
                        key="edit"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() =>
                          navigateTo(`/purchase-order/approval/${record.id}`)
                        }
                      >
                        Review
                      </Menu.Item>
                      {/* <Menu.Item
                        key="delete"
                        icon={<Trash strokeWidth={1.5} size={15} />}
                        onClick={() => {
                          setSelectedRow(record);
                          setAction("delete");
                          setShowModal("confirm");
                        }}
                        danger
                      >
                        Delete
                      </Menu.Item> */}
                    </>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "approved":
        return [
          {
            title: "Purchase Order #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Purchase Request #",
            dataIndex: "purchase_request_id",
            key: "purchase_request_id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Approved By",
            dataIndex: "approved_by_name",
            key: "approved_by_name",
          },
        ];
      case "disapproved":
        return [
          {
            title: "Purchase Order #",
            dataIndex: "id",
            key: "id",
          },
          {
            title: "Purchase Request #",
            dataIndex: "purchase_request_id",
            key: "purchase_request_id",
          },
          {
            title: "Transaction Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
          },
          {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            key: "supplier_name",
          },
          {
            title: "Payment Method",
            dataIndex: "payment_method",
            key: "payment_method",
            render: (value: any) => formatString(value) || "-",
          },
          {
            title: "Disapproved By",
            dataIndex: "disapproved_by_name",
            key: "disapproved_by_name",
          },
        ];
      default:
        return [];
    }
  }

  // const columns = [
  //   {
  //     title: "Supplier Name",
  //     dataIndex: "supplier_name",
  //     key: "supplier_name",
  //   },
  //   {
  //     title: "Transaction Date",
  //     dataIndex: "transaction_date",
  //     key: "transaction_date",
  //     render: (text: string) => dayjs(text).format("MMMM D, YYYY"), // ← format here
  //   },
  // ];

  async function submitForm(data: any) {
    setLoading("submit");
    var response = await postRequest(
      `purchase_orders/create`,
      { purchase_request_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
        navigateTo("/purchase-order")
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} purchase order.`
      );
    }
  }

  async function deleteExpense() {
    var response = await postRequest(
      `purchase_orders/delete`,
      {
        purchase_order_id: selectedRow.id,
      },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages[0] ??
            "Failed to delete purchase order."
        )
      );
    }
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Purchase Orders"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "admin", "supervisor")}
              addBtnText="Purchase Order"
              onCreate={() => navigateTo("/purchase-order/add")}
              withSearchBtn={hasRole(getUserRole(), "admin", "accounting")}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value ?? "",
                  page: 1,
                }))
              }
            />
          </Col>
          {!hasExactRole(getUserRole(), "supervisor") &&(
            <>
            <Col xs={24} className="mb-1">
              <span>
                <strong>Filter By:</strong>
              </span>
            </Col>
            <Col xs={24} md={12} lg={6} className="mb-3">
              <RangePicker
                picker="month"
                value={[
                  filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                  filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
                ]}
                format="MMMM YYYY" // e.g., "July 2025"
                onChange={(e) => handleFilterChange(e, "dates")}
                allowClear
                className="w-full"
              />
            </Col>
            </>
          )}
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="Pending" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                  <TabPane tab="Disapproved" key="disapproved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                      rowClassName={() => "cursor-pointer"}
                      onRow={(record: any) => ({
                        onClick: () => {
                          setSelectedRow(record);
                          form.setFieldsValue(record);
                          // setShowModal("update");
                        },
                      })}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default PurchaseOrder;
